import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { notifyLocaleChanged } from '../../i18n/uiText';
import { sortLanguagesByDisplayOrder } from '../../utils/sortLanguages';

const API_BASE = import.meta.env.VITE_API_URL || 'https://verbum-api-production.up.railway.app/api';

/** Полное имя перевода — для выпадающего списка (одна строка, без усечения в JS). */
function getTranslationFullLabel(translation) {
  if (!translation) return '';
  const name = String(translation.name || '').trim();
  if (name) return name;
  if (translation.shortName) return translation.shortName;
  return translation.id || '';
}

/** Короткая подпись в свёрнутом чипе шапки. */
function getTranslationTriggerLabel(translation) {
  if (!translation) return '';
  if (translation.shortName) return translation.shortName;
  const name = translation.name || '';
  const inParens = name.match(/\(([^)]+)\)/);
  if (inParens) return inParens[1].trim();
  return name.length > 10 ? name.slice(0, 9) + '…' : name;
}

export default function LanguageSelector({ currentTranslation }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    return localStorage.getItem('bibleLanguage') || 'en';
  });
  const [languages, setLanguages] = useState([]);
  const [availableTranslations, setAvailableTranslations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function loadLanguages() {
      try {
        const response = await fetch(`${API_BASE}/languages`);
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled) return;
        const langs = sortLanguagesByDisplayOrder(data.languages || []);
        setLanguages(langs);
        const langCode =
          typeof window !== 'undefined' ? localStorage.getItem('bibleLanguage') || 'en' : 'en';
        const currentLang = langs.find((lang) => lang.code === langCode) || langs[0];
        if (currentLang) setAvailableTranslations(currentLang.translations || []);
      } catch (e) {
        console.error('Error loading languages', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadLanguages();
    return () => {
      cancelled = true;
    };
  }, []);

  // Текущий перевод в URL нужен только для отображения доступных переводов и активного языка
  // в дропдауне. Не перезаписываем выбранный пользователем язык интерфейса автоматически.
  useEffect(() => {
    if (!languages.length || !currentTranslation) return;
    const owner = languages.find(
      (lang) =>
        Array.isArray(lang.translations) &&
        lang.translations.some((t) => t.id === currentTranslation)
    );
    if (!owner) return;
    setSelectedLanguage((prev) => (prev === owner.code ? prev : owner.code));
  }, [languages, currentTranslation]);

  useEffect(() => {
    const currentLang = languages.find(lang => lang.code === selectedLanguage);
    if (currentLang) {
      setAvailableTranslations(currentLang.translations || []);
    }
  }, [selectedLanguage, languages, currentTranslation]);

  const handleTranslationChange = (translationId) => {
    if (translationId === currentTranslation) return;
    const owner = languages.find(
      (lang) =>
        Array.isArray(lang.translations) &&
        lang.translations.some((t) => t.id === translationId)
    );
    if (typeof window !== 'undefined') {
      localStorage.setItem('bibleTranslation', translationId);
      if (owner) {
        localStorage.setItem('bibleLanguage', owner.code);
        notifyLocaleChanged();
      }
    }
    if (owner) setSelectedLanguage(owner.code);
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts.length > 0) {
      pathParts[0] = translationId;
      navigate('/' + pathParts.join('/'), { replace: true });
    }
    setIsOpen(false);
  };

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    function handleScroll() {
      setIsOpen(false);
    }
    document.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    return () => document.removeEventListener('scroll', handleScroll, { capture: true });
  }, []);

  if (loading) {
    return (
      <div className="chapter-header-version-dropdown">
        <span className="chapter-header-version-dropdown-trigger">...</span>
      </div>
    );
  }

  if (availableTranslations.length <= 1) {
    return null;
  }

  const current = availableTranslations.find(t => t.id === currentTranslation) || availableTranslations[0];

  return (
    <div
      className={`chapter-header-version-dropdown${isOpen ? ' chapter-header-version-dropdown--open' : ''}`}
      ref={dropdownRef}
    >
      <button
        type="button"
        className="chapter-header-version-dropdown-trigger"
        onClick={() => setIsOpen(!isOpen)}
        title={getTranslationFullLabel(current) || undefined}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span>
          {current ? (isOpen ? getTranslationFullLabel(current) : getTranslationTriggerLabel(current)) : '—'}
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`chapter-header-version-dropdown-chevron ${isOpen ? 'open' : ''}`}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {isOpen && (
        <ul className="chapter-header-version-dropdown-list" role="listbox">
          {availableTranslations.map((translation) => (
            <li key={translation.id}>
              <button
                type="button"
                role="option"
                aria-selected={currentTranslation === translation.id}
                className={`chapter-header-version-dropdown-option ${currentTranslation === translation.id ? 'active' : ''}`}
                onClick={() => handleTranslationChange(translation.id)}
                title={getTranslationFullLabel(translation)}
              >
                {getTranslationFullLabel(translation)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
